import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const faqs = [
  { q: '¿Qué es The Screen Squad?', a: 'The Screen Squad es un servicio profesional de reparación, reemplazo e instalación de pantallas y mosquiteros para ventanas y puertas en Santa Ana, California y áreas cercanas. Nos especializamos en pantallas nuevas, reparaciones rápidas, puertas corredizas y servicio a domicilio. Ofrecemos atención rápida, profesional y confiable.' },
  { q: '¿Qué áreas atienden?', a: 'Atendemos Santa Ana, Orange County y áreas cercanas en California. Ofrecemos servicios tanto para propiedades residenciales como comerciales en toda el área metropolitana.' },
  { q: '¿Cómo solicitan sus precios?', a: 'Ofrecemos presupuestos gratuitos sin compromiso. Simplemente contáctanos por teléfono, correo electrónico o nuestro formulario en línea. Visitaremos tu propiedad para evaluar tus pantallas y te proporcionaremos un presupuesto detallado y justo.' },
  { q: '¿Qué tipos de pantallas reparan?', a: 'Reparamos e instalamos todo tipo de pantallas: ventanas, puertas corredizas, puertas francesas, patios, y más. También ofrecemos pantallas con puertas para mascotas y pantallas de seguridad. Usamos materiales de alta calidad para garantizar durabilidad.' },
  { q: '¿Tienen garantía?', a: 'Sí, garantizamos la satisfacción del cliente en cada proyecto. Si algo no está bien, volvemos y lo arreglamos sin costo adicional. Tu satisfacción es nuestra prioridad. Bugs Out!' },
];

export default function AboutPage() {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const features = [
    { icon: 'ri-shield-check-line', key: 'experience' },
    { icon: 'ri-building-line', key: 'residential' },
    { icon: 'ri-hammer-line', key: 'materials' },
    { icon: 'ri-price-tag-3-line', key: 'pricing' },
    { icon: 'ri-heart-line', key: 'satisfaction' },
  ];

  const serviceTags = [
    { icon: 'ri-window-line', label: t('about.services.sprayFoam') },
    { icon: 'ri-door-line', label: t('about.services.battRoll') },
    { icon: 'ri-tools-line', label: t('about.services.atticWall') },
    { icon: 'ri-truck-line', label: t('about.services.commercial') },
  ];

  return (
    <>
      <Navbar />
      <main>
        {/* Hero Banner */}
        <section className="relative w-full min-h-[360px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              alt="The Screen Squad screen repair work"
              className="w-full h-full object-cover object-center"
              src="https://storage.readdy-site.link/project_files/95272ccd-89f8-41e5-a90f-f2c2b48cc48a/a783fbb1-e44e-4155-8045-3a0decf4c936_compressed_magnific_reframe-this-exact-photog_e8TFD5ldqL.webp"
            />
            <div className="absolute inset-0 bg-black/60" />
          </div>
          <div className="relative z-10 text-center mt-12">
            <span className="inline-block bg-black/80 border-2 border-primary-500 text-white text-2xl md:text-4xl font-extrabold tracking-[0.15em] uppercase px-20 py-6 rounded-lg backdrop-blur-sm">
              {t('aboutPage.heroTitle')}
            </span>
          </div>
        </section>

        {/* About Story */}
        <section className="py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-14">
              <div className="w-full lg:w-[55%] flex justify-center">
                <div className="relative w-full max-w-[540px]">
                  <img
                    alt="The Screen Squad team member working on screen door"
                    className="w-full h-auto max-h-[600px] object-contain rounded-2xl"
                    src="https://storage.helloreaddy.io/project_files/95272ccd-89f8-41e5-a90f-f2c2b48cc48a/ff18ccb2-fabe-4fa7-9afe-70d7d92d29bb_compressed_WhatsApp-Image-2026-07-13-at-11.23.51-PM.webp"
                  />
                  <div className="absolute bottom-6 left-6 bg-primary-500 text-white px-4 py-3 rounded-lg">
                    <p className="text-2xl font-extrabold">The</p>
                    <p className="text-xs font-medium mt-0.5">Screen Squad</p>
                  </div>
                  <div className="absolute bottom-6 right-6 bg-primary-500 text-white text-xs font-bold px-4 py-2 rounded-lg uppercase tracking-wider">
                    Santa Ana, California
                  </div>
                </div>
              </div>
              <div className="w-full lg:w-[45%]">
                <span className="text-primary-500 text-xs font-bold uppercase tracking-[0.25em]">{t('aboutPage.storyLabel')}</span>
                <h2 className="text-3xl md:text-4xl font-extrabold text-foreground-50 mt-3 leading-tight">
                  {t('aboutPage.storyTitle')}
                </h2>
                <p className="text-foreground-400 mt-5 text-base leading-relaxed">
                  {t('about.description1')}
                </p>
                <p className="text-foreground-400 mt-3 text-base leading-relaxed">
                  {t('about.description2')}
                </p>
                <p className="text-foreground-50 font-semibold text-base mt-6 italic">
                  {t('about.tagline')}
                </p>

                {/* Stats Row */}
                <div className="flex flex-wrap gap-8 mt-8">
                  <div>
                    <p className="text-2xl font-extrabold text-primary-500">15+</p>
                    <p className="text-foreground-400 text-xs mt-0.5 uppercase tracking-wider">{t('aboutPage.stats.experienceLabel')}</p>
                  </div>
                  <div>
                    <p className="text-2xl font-extrabold text-primary-500">100%</p>
                    <p className="text-foreground-400 text-xs mt-0.5 uppercase tracking-wider">{t('aboutPage.stats.satisfactionLabel')}</p>
                  </div>
                  <div>
                    <p className="text-xl font-extrabold text-primary-500">{t('aboutPage.stats.quotesNumber')}</p>
                    <p className="text-foreground-400 text-xs mt-0.5 uppercase tracking-wider">{t('aboutPage.stats.quotesLabel')}</p>
                  </div>
                </div>

                {/* Service Tags */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-8">
                  {serviceTags.map((s, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-6 h-6 flex items-center justify-center shrink-0">
                        <i className={`${s.icon} text-primary-500 text-sm w-4 h-4 flex items-center justify-center`} />
                      </div>
                      <span className="text-foreground-400 text-sm font-medium">{s.label}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to="/services"
                  className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold px-7 py-3.5 rounded-full text-sm transition-colors cursor-pointer mt-8"
                >
                  {t('about.cta')}
                  <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Vision */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0">
            <img
              alt="Screen materials and tools close up"
              className="w-full h-full object-cover object-center"
              src="https://storage.readdy-site.link/project_files/95272ccd-89f8-41e5-a90f-f2c2b48cc48a/696abc18-c46e-42f5-8b40-ec4c7f1f3b60_compressed_magnific_create-an-ultrarealistic-_xg565cOjfW.webp"
            />
            <div className="absolute inset-0 bg-black/70" />
          </div>
          <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-6">{t('aboutPage.visionLabel')}</h2>
            <p className="text-white/70 text-base leading-relaxed max-w-2xl mx-auto">
              {t('aboutPage.visionText')}
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold px-7 py-3.5 rounded-full text-sm transition-colors cursor-pointer mt-8"
            >
              {t('aboutPage.ctaQuote')}
              <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center" />
            </Link>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {/* Header */}
            <div className="mb-10">
              <span className="text-primary-500 text-xs font-bold uppercase tracking-[0.25em]">{t('aboutPage.faqLabel')}</span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-foreground-50 mt-3 leading-tight">
                {t('aboutPage.faqTitle')}
              </h2>
              <p className="text-foreground-400 mt-4 text-sm leading-relaxed max-w-xl">
                {t('aboutPage.faqDesc')}
              </p>
            </div>
            {/* Content: FAQ + Image side by side */}
            <div className="flex flex-col lg:flex-row gap-8">
              {/* FAQ Column */}
              <div className="w-full lg:w-[55%] flex flex-col gap-3">
                {faqs.map((faq, i) => (
                  <div
                    key={i}
                    className="border border-white/10 rounded-lg overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="w-full flex items-center justify-between px-5 py-3.5 text-left cursor-pointer"
                    >
                      <span className="text-primary-500 font-semibold text-sm">{faq.q}</span>
                      <div className={`w-6 h-6 flex items-center justify-center rounded-full bg-primary-500 shrink-0 transition-transform ${openFaq === i ? 'rotate-45' : ''}`}>
                        <i className="ri-add-line text-white text-xs" />
                      </div>
                    </button>
                    {openFaq === i && (
                      <div className="px-5 pb-4">
                        <p className="text-foreground-400 text-sm leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {/* Image Column */}
              <div className="w-full lg:w-[45%] lg:self-start">
                <div className="rounded-xl overflow-hidden h-[320px] lg:h-[340px]">
                  <img
                    alt="Screen door installation project"
                    className="w-full h-full object-cover object-center rounded-xl"
                    src="https://storage.readdy-site.link/project_files/95272ccd-89f8-41e5-a90f-f2c2b48cc48a/b22c1235-560f-4596-99f8-956f49d44a51_compressed_magnific_reframe-this-exact-photog_l7rZLUbgv9.webp"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Approach */}
        <section className="py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-14 items-center">
              <div className="w-full lg:w-[45%]">
                <div className="relative rounded-2xl overflow-hidden">
                  <img
                    alt="The Screen Squad professional screen installation"
                    className="w-full h-auto object-cover"
                    src="https://storage.readdy-site.link/project_files/95272ccd-89f8-41e5-a90f-f2c2b48cc48a/03a35747-c098-4475-a5f1-696183d79ce4_compressed_WhatsApp-Image-2026-07-13-at-11.22.33-PM.webp"
                  />
                  <div className="absolute bottom-4 left-4 bg-primary-500 text-white text-xs font-bold px-3 py-1.5 rounded-full">
                    THE SCREEN SQUAD
                  </div>
                </div>
              </div>
              <div className="w-full lg:w-[55%]">
                <span className="text-primary-500 text-xs font-bold uppercase tracking-[0.25em]">{t('aboutPage.approachLabel')}</span>
                <h2 className="text-3xl md:text-4xl font-extrabold text-foreground-50 mt-3 leading-tight">
                  {t('aboutPage.approachTitle')}
                </h2>
                <p className="text-foreground-400 mt-4 text-base leading-relaxed">
                  {t('aboutPage.approachDesc')}
                </p>
                <div className="flex flex-col gap-4 mt-8">
                  {features.map((f, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-primary-500/15 rounded-lg shrink-0">
                        <i className={`${f.icon} text-primary-500 text-lg w-6 h-6 flex items-center justify-center`} />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-foreground-50 font-semibold text-sm">
                          {t(`aboutPage.features.${f.key}` as const)}
                        </h4>
                        <p className="text-foreground-400 text-xs mt-1 leading-relaxed">
                          {t(`aboutPage.features.${f.key}Desc` as const)}
                        </p>
                      </div>
                      <div className="w-6 h-6 flex items-center justify-center shrink-0">
                        <i className="ri-check-line text-primary-500 w-5 h-5 flex items-center justify-center" />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-4 mt-8">
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold px-7 py-3.5 rounded-full text-sm transition-colors cursor-pointer"
                  >
                    {t('aboutPage.ctaQuote')}
                    <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center" />
                  </Link>
                  <Link
                    to="/services"
                    className="inline-flex items-center gap-2 border border-white/10 hover:border-primary-500 text-foreground-50 font-semibold px-7 py-3.5 rounded-full text-sm transition-colors cursor-pointer"
                  >
                    {t('aboutPage.ctaViewAll')}
                    <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}